import { useState, useEffect, useMemo, useContext } from 'react';
import styles from '../Applicants/css/display-applicant.module.css';
import { useQuery } from '@tanstack/react-query';
import { FilterDropDown } from '../Applicants/Applicants'; 
import { AiOutlineSearch as SearchIcon } from 'react-icons/ai';
import { IoReload as ReloadIcon } from 'react-icons/io5';
import apiCall from '../../../utils/apiCall';
import { CircularProgress, styled } from '@mui/material';
import { AxiosError } from 'axios';
import { ExNyRoute, PrRoute, ExRoute, AcRoute, MiRoute, CoRoute } from '../../../utils/allRouteId';
import { RouteResponse } from '../../../types/index';
import { FetchAllRoutes, reOrderFetchedData } from '../../../hooks/FetchData';
import { Button, DateFilter } from '../../../ui-components';
import { NotificationContext } from '../../../providers/notification';
import { apiRequest, config } from '../../../api';
import fileDownload from 'js-file-download';
import { convertTimetoString } from "../../../helperFunction";


export interface Applicants {
  ApplicantId: string;

  FirstName: string;
  MobilePhone: string;
  Email: string;

  LastName: string;
  MembersipNo: string;

  RouteName: string;
  InductionDate: string;

  MembershipGenerated: string;

  Trainings: string;
  Trainings_Score: string;

  Present_Employer: string;
  Designation: string;
  Designation_Score: string;

  Total_Score: string;

  Year_experience: string;
  Remarks: string;
  PreviousOrganization: string;
  PreviousWorkStartYear: string;
  PreviousWorkEndYear: string;
  PreviousPositionHeld: string;

  AcademicQualification: string;
  YearOfDegree: string;

  Hr_Experience_Year: string;

  Core_HR_Specialization: string;
  Core_HR_StartYear: string;
  Core_HR_EndYear: string;
}

interface DisplayApplicantsProps {
  setRouteName: React.Dispatch<React.SetStateAction<string>>;
}

const option1:any= [
    // {val:1, name:"Memembership Route",path:`${BASE_URL}`},
     {val: ExNyRoute, name:"Examination / NYSC"},
    {val:PrRoute, name:"Practitioner"},
    {val:ExRoute, name:"Executive"},
    {val: AcRoute, name:"Academic"},
    {val: MiRoute, name:"Military"},
    {val:CoRoute, name:"Corporate"}
  ]

const statusOptions = [
  { val: 1, name: "Pending Approval" },
  { val: 2, name: "Approved" }
];

const DisplayApplicant = ({ setRouteName }: DisplayApplicantsProps) => {
  const { showNotification } = useContext(NotificationContext);

  const [filters, setFilters] = useState({
    routeId: Number(ExRoute),
    searchData: '',
    date: { start: '', end: '' },
  });

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 1000;

  const [d_loading, set_d_loading] = useState(false);
  const [routeNameInt, setRouteNameInt] = useState('');

  const formatDate = (date?: string) => {
    if (!date) return 'N/A';

    const parsed = new Date(date);

    if (isNaN(parsed.getTime())) return 'N/A';

    return parsed.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getAllApplicants = async () => {
    const params = new URLSearchParams();

    params.append('PageNumber', currentPage.toString());
    params.append('PageSize', pageSize.toString());
    params.append('RouteId', filters.routeId.toString());

    if (filters.date.start) {
      params.append('startDate', formatDate(filters.date.start));
    }

    if (filters.date.end) {
      params.append('endDate', formatDate(filters.date.end));
    }

    const response = await apiCall().get(
      `/Activity/GetAllMembership?${params.toString()}`
    );

    // ✅ DEBUG: Log the API response
    console.log('API Response:', response.data);

    return response.data;
  };

  const GetAllMembershipQuery = useQuery(
    [
      'get-All-Applicant',
      filters.routeId,
      filters.searchData,
      filters.date.start,
      filters.date.end,
      currentPage,
    ],
    getAllApplicants,
    {
      retry: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const apiData = GetAllMembershipQuery.data;
  const allApplicants = apiData?.Data ?? [];

  // ✅ FIXED: Handle different API response structures
  const totalCount = apiData?.TotalSize ?? 
                    apiData?.totalCount ?? 
                    apiData?.total ?? 
                    apiData?.count ?? 
                    allApplicants.length;

  const pageCount = Math.ceil(totalCount / pageSize);

  const startRecord =
    totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;

  const endRecord = Math.min(currentPage * pageSize, totalCount);

  /* ================= ROUTES ================= */
  const { isLoading, data } = FetchAllRoutes();
  const reOrderedAllRoutes = data
    ? reOrderFetchedData((data as RouteResponse)?.Data)
    : [];

  const allRoutes = isLoading
    ? []
    : reOrderedAllRoutes.map(item => ({
        name: item.RouteName,
        val: item.RouteId,
      }));

  const switchRouteId = (route: { val: number; name: string }) => {
    setFilters(prev => ({ ...prev, routeId: route.val }));
    setRouteName(route.name);
    setRouteNameInt(route.name);
    setCurrentPage(1);
  };

  /* ================= FIXED DOWNLOAD ================= */
   const handleDownload = async () => {
    set_d_loading(true);

    try {
      const params = new URLSearchParams();
      params.append('routeId', filters.routeId.toString());

      if (filters.searchData.trim()) {
        params.append('searchQuery', filters.searchData.trim());
      }

      if (filters.date.start) {
        params.append('startDate', filters.date.start);
      }

      if (filters.date.end) {
        params.append('endDate', filters.date.end);
      }

      // ✅ FIXED: Use POST method for download (405 was because it expects POST)
      const response = await apiCall().post(
        `/Activity/DownloadAllMembership?${params.toString()}`,
        {}, // Empty body for POST
        {
          responseType: 'blob', // ✅ Critical: Handle as blob
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // ✅ DEBUG: Check response
      console.log('Download response:', response);

      // ✅ Create blob URL and trigger download
      const contentType = response.headers['content-type'];
      const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `applicants-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification({
        message: 'File downloaded successfully!',
        type: 'success',
      });
    } catch (error: any) {
      console.error('Download error:', error);
      
      // ✅ Better error handling
      if (error.response?.status === 405) {
        showNotification({
          message: 'Download endpoint requires POST method. Check backend.',
          type: 'error',
        });
      } else if (error.response?.status === 404) {
        showNotification({
          message: 'Download endpoint not found. Check URL.',
          type: 'error',
        });
      } else if (error.response?.data?.Message) {
        showNotification({
          message: error.response.data.Message,
          type: 'error',
        });
      } else {
        showNotification({
          message: `Download failed: ${error.message}`,
          type: 'error',
        });
      }
    } finally {
      set_d_loading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.filterDiv}>
        <label className={styles.searchForm}>
          <span className={styles.sI}>
            <SearchIcon />
          </span>
          <input
            type="text"
            placeholder="Search applicants..."
            value={filters.searchData}
            onChange={e => {
              setFilters(prev => ({ ...prev, searchData: e.target.value }));
              setCurrentPage(1);
            }}
          />
        </label>

        <FilterDropDown
          onChange={switchRouteId}
          text={routeNameInt || 'Membership Route'}
          bg="#0A3922"
          color="#fff"
          options={allRoutes}
        />

        <DateFilter
          onChange={(date: any) => {
            setFilters(prev => ({ ...prev, date }));
            setCurrentPage(1);
          }}
        />

        <Button
          onClick={handleDownload}
          children={d_loading ? 'Downloading...' : 'Download'}
          border="#E2E4E9"
          bg="#0A3922"
          color="#fff"
          disabled={d_loading}
        />
      </div>

      {/* ================= RECORD SUMMARY ================= */}
      {!GetAllMembershipQuery.isLoading && (
        <div className={styles.recordBar}>
          Showing <strong>{startRecord}</strong>–<strong>{endRecord}</strong> of{" "}
          <strong>{totalCount}</strong> applicants
        </div>
      )}

      {/* ================= CONTENT ================= */}
      {GetAllMembershipQuery.isLoading ? (
        <div className={styles.loaderWrapper}>
          <CircularProgress />
        </div>
      ) : allApplicants.length ? (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.modernTable}>
              <thead>
                <tr>
                  <th>FirstName</th>
                  <th>LastName</th>
                  <th>MembersipNo</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>RouteName</th>
                  <th>InductionDate</th>
                  <th>Membership date</th>
                </tr>
              </thead>

              <tbody>
                {allApplicants.map((applicant: Applicants, index: number) => {
                  const total = Number(applicant.Total_Score) || 0;

                  return (
                    <tr key={applicant.ApplicantId}>
                      <td className={styles.nameCell}>
                        {applicant.FirstName || 'N/A'}
                      </td>
                      <td className={styles.nameCell}>
                        {applicant.LastName || 'N/A'}
                      </td>
                      <td>{applicant.MembersipNo || 'N/A'}</td>
                      <td>{applicant.Email || 'N/A'}</td>
                      <td>{applicant.MobilePhone || 'N/A'}</td>
                      <td>{applicant.RouteName || 'N/A'}</td>
                      <td>{convertTimetoString(applicant?.InductionDate, 0) || "N/A"}</td>
                      <td>{convertTimetoString(applicant?.MembershipGenerated, 0) || "N/A"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ================= MODERN PAGINATION ================= */}
          <div className={styles.pagination}>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
              className={styles.pageButton}
            >
              Prev
            </button>

            <span className={styles.pageInfo}>
              Page {currentPage} of {pageCount}
            </span>

            <button
              disabled={currentPage === pageCount}
              onClick={() => setCurrentPage(p => p + 1)}
              className={styles.pageButton}
            >
              Next
            </button>
          </div>
        </>
      ) : (
        <NoResult>No Results</NoResult>
      )}
    </div>
  );
};

export default DisplayApplicant;

const StyledError = styled('p')`
  color: red;
  font-size: 1.4rem;
  display: flex;
`;

const NoResult = styled('div')`
  margin-block-start: 0px;
`;
